'use client'

/**
 * useActivityVisibility - Unified visibility hook
 *
 * Reads `settings/itemVisibility` from Firestore to determine which
 * adventures and experiences are visible on the public website.
 *
 * Usage:
 *   const { isVisible } = useActivityVisibility()
 *   adventures.filter(a => isVisible(a.id, a.hidden))
 *
 * - While Firestore loads: falls back to the static `hidden` field
 * - After load: uses Firestore as the single source of truth
 * - Items not in Firestore: fall back to static `hidden` field
 *
 * Module-level cache prevents duplicate Firestore reads across components.
 */

import { useEffect, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'

interface VisibilityMap {
  [itemId: string]: boolean
}

interface UseActivityVisibilityReturn {
  /** Whether an item should be shown. Pass the static `hidden` field as fallback. */
  isVisible: (id: string, staticHidden?: boolean) => boolean
  /** Whether Firestore data has loaded */
  loaded: boolean
}

// Module-level cache — shared across all components on the same page
let cachedVisibility: VisibilityMap | null = null
let fetchPromise: Promise<VisibilityMap | null> | null = null

async function fetchVisibility(): Promise<VisibilityMap | null> {
  try {
    const docRef = doc(db, 'settings', 'itemVisibility')
    const snapshot = await getDoc(docRef)
    if (snapshot.exists()) {
      return snapshot.data() as VisibilityMap
    }
    return null
  } catch (error) {
    console.error('[useActivityVisibility] Error fetching visibility:', error)
    return null
  }
}

export function useActivityVisibility(): UseActivityVisibilityReturn {
  const [visibility, setVisibility] = useState<VisibilityMap | null>(cachedVisibility)
  const [loaded, setLoaded] = useState(cachedVisibility !== null)

  useEffect(() => {
    // Already cached from a previous render
    if (cachedVisibility !== null) {
      setVisibility(cachedVisibility)
      setLoaded(true)
      return
    }

    // Deduplicate concurrent fetches
    if (!fetchPromise) {
      fetchPromise = fetchVisibility()
    }

    let cancelled = false

    fetchPromise.then((data) => {
      if (!cancelled) {
        cachedVisibility = data
        setVisibility(data)
        setLoaded(true)
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  const isVisible = (id: string, staticHidden?: boolean): boolean => {
    // Firestore not loaded yet — use static fallback
    if (visibility === null) {
      return !staticHidden
    }
    // Firestore has this item — use it as source of truth
    if (id in visibility) {
      return visibility[id]
    }
    // Item not in Firestore — use static fallback
    return !staticHidden
  }

  return { isVisible, loaded }
}

/**
 * Extract the slug/ID from a navigation URL.
 * e.g. "/guided-adventures/resort-skiing" → "resort-skiing"
 *      "/experiences/wine-route" → "wine-route"
 */
export function extractIdFromUrl(url: string): string {
  return url.split('/').pop() || ''
}
